import React, { useState, useEffect, useRef, useMemo } from "react";
import { Checkbox } from "./ui/checkbox";
import { galleryAvailable, saveToGallery, getSaveToGalleryPref, setSaveToGalleryPref } from "../lib/gallery";
import { api, sendMessage, uploadMedia, dataUrlToBase64, compressImage, fmtDate } from "../api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MarkupEditor } from "./MarkupEditor";
import { toast } from "sonner";
import { AlertTriangle, Ruler, Package, HelpCircle, PenLine, Briefcase, Search, CheckSquare, Camera, X, Video, Mic, Square as SquareIcon, Loader2, Sparkles, Download, Stamp, Image as ImageIcon, Plus, CalendarDays } from "lucide-react";
import { Switch } from "./ui/switch";
import { saveImageDataUrl, mediaFilename } from "../lib/share";
import { VoiceInputButton } from "./VoiceInputButton";
import { VoiceDraftButton } from "./VoiceDraftButton";
import { annotateMeasurePhoto, sizeEquals } from "../lib/measureAnnotate";
import { warmMic, getMic, releaseMic } from "../lib/mic";

const CATEGORIES = ["before", "during", "issue", "correction", "after"];

// ---- Job picker period filter (scheduled_date, local calendar, weeks start Sunday) ----
const PERIODS = ["all", "today", "this_week", "last_week", "next_week", "this_month", "last_month", "next_month"];
const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
function periodRange(key) {
  const today = dayStart(new Date());
  const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  const weekStart = addDays(today, -today.getDay());
  const monthStart = (y, m) => new Date(y, m, 1);
  switch (key) {
    case "today": return [today, addDays(today, 1)];
    case "this_week": return [weekStart, addDays(weekStart, 7)];
    case "last_week": return [addDays(weekStart, -7), weekStart];
    case "next_week": return [addDays(weekStart, 7), addDays(weekStart, 14)];
    case "this_month": return [monthStart(today.getFullYear(), today.getMonth()), monthStart(today.getFullYear(), today.getMonth() + 1)];
    case "last_month": return [monthStart(today.getFullYear(), today.getMonth() - 1), monthStart(today.getFullYear(), today.getMonth())];
    case "next_month": return [monthStart(today.getFullYear(), today.getMonth() + 1), monthStart(today.getFullYear(), today.getMonth() + 2)];
    default: return null;
  }
}
const jobDate = (j) => (j.scheduled_date ? dayStart(new Date(j.scheduled_date)) : null);
// Active (today) first; then upcoming soonest-first; then past most-recent-first
function jobPickerSort(a, b) {
  const rank = { active: 0, future: 1, past: 2 };
  const ra = rank[a.time_bucket] ?? 3, rb = rank[b.time_bucket] ?? 3;
  if (ra !== rb) return ra - rb;
  const da = jobDate(a)?.getTime() ?? 0, db = jobDate(b)?.getTime() ?? 0;
  return ra === 2 ? db - da : da - db;
}

export function JobPickerDialog({ open, onClose, onPick, title, zClass = "" }) {
  const { user, t, lang } = useApp();
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [crewFilter, setCrewFilter] = useState("all");
  const [period, setPeriod] = useState("all");

  useEffect(() => {
    // Installers only see THEIR OWN jobs in the picker. Supervisors and
    // admins may link ANY job in the system (scope=all skips the
    // supervisor-only restriction server-side).
    if (open) {
      const scope = user.role === "installer" ? "" : "&scope=all";
      api.get(`/jobs?user_id=${user.id}${scope}`).then((res) => setJobs(res.data));
      setCrewFilter("all");
      setPeriod("all");
      setSearch("");
    }
  }, [open, user.id, user.role]);

  // Crew filter chips built from the VISIBLE jobs — crews of connected
  // partner companies (PM1, PM2, ...) show up automatically because their
  // synced jobs carry the crew reference.
  const crewChips = useMemo(() => {
    // keyed by LABEL (PM1, PM2...) — the same crew mirrored by two companies is one chip
    const m = new Map();
    jobs.forEach((j) => {
      const c = j.crew;
      const label = c && (c.code || c.name);
      if (label && !m.has(label)) m.set(label, { id: label, label });
    });
    return [...m.values()].sort((a, b) => (a.label || "").localeCompare(b.label || "", undefined, { numeric: true }));
  }, [jobs]);

  const q = search.toLowerCase();
  const range = periodRange(period);
  const inPeriod = (j) => {
    if (!range) return true;
    const d = jobDate(j);
    return !!d && d >= range[0] && d < range[1];
  };
  const filtered = jobs.filter((j) =>
    (crewFilter === "all" || (j.crew?.code || j.crew?.name) === crewFilter) && inPeriod(j) &&
    (!q || j.job_number.includes(q) || j.customer_name.toLowerCase().includes(q) || j.address.toLowerCase().includes(q)))
    .sort(jobPickerSort);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* flex column capped to the (keyboard-aware) viewport: only the job LIST scrolls,
          never the whole modal; the crew chips row scrolls sideways (min-w-0 is what lets
          an overflow-x row actually scroll inside a grid/flex parent). */}
      <DialogContent
        className={`max-w-md flex flex-col max-h-[85dvh] overflow-hidden ${zClass}`}
        data-testid="job-picker-dialog"
        onOpenAutoFocus={(e) => { if (window.innerWidth < 768) e.preventDefault(); }}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-display flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary" /> {title || t("select_job")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 shrink-0">
          <div className="relative flex-1 min-w-0">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="job-picker-search" placeholder={t("search_jobs")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" autoFocus={typeof window !== "undefined" && window.innerWidth >= 768} />
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className={`w-[9.5rem] shrink-0 ${period !== "all" ? "border-primary text-primary" : ""}`} data-testid="job-picker-period">
              <CalendarDays className="h-4 w-4 mr-1.5 shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={zClass || "z-[200]"}>
              {PERIODS.map((k) => (
                <SelectItem key={k} value={k} data-testid={`job-picker-period-${k}`}>{t(`period_${k}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {crewChips.length >= 2 && (
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 min-w-0 w-[calc(100%+0.5rem)] shrink-0 [scrollbar-width:none] [-webkit-overflow-scrolling:touch]" style={{ touchAction: "pan-x" }} data-testid="job-picker-crew-filters">
            <Badge
              data-testid="job-picker-crew-all"
              variant={crewFilter === "all" ? "default" : "outline"}
              onClick={() => setCrewFilter("all")}
              className="cursor-pointer shrink-0 select-none"
            >
              {t("all_crews")}
            </Badge>
            {crewChips.map((c) => (
              <Badge
                key={c.id}
                data-testid={`job-picker-crew-${c.label}`}
                variant={crewFilter === c.id ? "default" : "outline"}
                onClick={() => setCrewFilter(crewFilter === c.id ? "all" : c.id)}
                className="cursor-pointer shrink-0 select-none"
              >
                {c.label}
              </Badge>
            ))}
          </div>
        )}
        <div className="flex-1 min-h-0 max-h-[45vh] overflow-y-auto -mx-2 px-2 space-y-0.5 overscroll-contain" style={{ touchAction: "pan-y" }} data-testid="job-picker-list">
          {filtered.map((j) => (
            <div
              key={j.id}
              data-testid={`job-picker-row-${j.job_number}`}
              onClick={() => onPick(j)}
              className="px-3 py-2.5 rounded-lg hover:bg-muted/60 cursor-pointer transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="font-mono-plex text-xs font-semibold text-primary">#{j.job_number}</span>
                <span className="text-sm font-medium truncate">{j.customer_name}</span>
                {j.crew?.code || j.crew?.name ? (
                  <Badge variant="outline" className="text-[9px] shrink-0">{j.crew.code || j.crew.name}</Badge>
                ) : null}
                <Badge variant={j.time_bucket === "active" ? "default" : "secondary"} className="text-[9px] ml-auto shrink-0" data-testid={`job-picker-status-${j.job_number}`}>
                  {j.time_bucket === "active" ? t("active") : t(j.status)}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground truncate mt-0.5">
                {j.scheduled_date && <span className="font-medium text-foreground/70">{fmtDate(j.scheduled_date, lang)} · </span>}
                {j.address}, {j.city}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center text-sm text-muted-foreground py-8">—</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function IssueDialog({ open, onClose, conversation, jobId, prefill, onCreated }) {
  const { user, lang, t } = useApp();
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("high");
  const [saving, setSaving] = useState(false);
  const [mediaItems, setMediaItems] = useState([]); // {id, kind, preview, name}
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const mediaRef = useRef(null);
  // Voice-first flow: speak the problem -> AI drafts the issue -> add media -> create
  const [voiceState, setVoiceState] = useState("idle"); // idle | recording | processing | ready
  const voiceRecRef = useRef(null);
  const voiceChunksRef = useRef([]);

  useEffect(() => {
    if (open && prefill) {
      setDescription(prefill.description || "");
      setTitle(prefill.title || (prefill.description || "").slice(0, 60));
    }
    if (open) {
      setMediaItems([]);
      setVoiceState("idle");
      warmMic().catch(() => {}); // instant recording on tap
    }
    if (!open) releaseMic(1500);
  }, [open, prefill]);

  const startVoice = async () => {
    try {
      const stream = await getMic();
      const mr = new MediaRecorder(stream);
      voiceChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) voiceChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        setVoiceState("processing");
        try {
          const blob = new Blob(voiceChunksRef.current, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, "issue.webm");
          const res = await api.post("/transcribe", fd); // authenticated (AI consent gate)
          const text = (res.data.transcript || "").trim();
          if (!text) {
            toast.info(t("no_speech_detected"));
            setVoiceState("idle");
            return;
          }
          // AI turns the spoken report into title/description/location/priority
          const draft = await api.post("/ai/issue-draft", { text, language: lang });
          setTitle(draft.data.title || text.slice(0, 60));
          setDescription(draft.data.description || text);
          if (draft.data.location) setLocation(draft.data.location);
          if (draft.data.priority) setPriority(draft.data.priority);
          setVoiceState("ready");
        } catch (e) {
          toast.error("Failed");
          setVoiceState("idle");
        }
      };
      mr.start();
      voiceRecRef.current = mr;
      setVoiceState("recording");
    } catch (e) {
      toast.error(t("mic_denied"));
    }
  };

  const stopVoice = () => {
    try { voiceRecRef.current?.stop(); } catch (e) { /* ignore */ }
  };

  const effectiveJobId = jobId || conversation?.job_id;

  const onPickMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploadingMedia(true);
    try {
      for (const file of files) {
        if (file.type.startsWith("video/")) {
          if (file.size > 8 * 1024 * 1024) {
            toast.error("Max 8MB");
            continue;
          }
          const b64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          const m = await uploadMedia({ base64: b64, contentType: file.type, kind: "video", jobId: effectiveJobId, uploadedBy: user.id, filename: file.name });
          setMediaItems((prev) => [...prev, { id: m.id, kind: "video", preview: null, name: file.name }]);
        } else {
          const { base64, dataUrl } = await compressImage(file);
          const m = await uploadMedia({ base64, jobId: effectiveJobId, category: "issue", uploadedBy: user.id });
          setMediaItems((prev) => [...prev, { id: m.id, kind: "image", preview: dataUrl }]);
        }
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingMedia(false);
    }
  };

  const submit = async () => {
    if (!title.trim() || !effectiveJobId) return;
    setSaving(true);
    try {
      await api.post("/issues", {
        job_id: effectiveJobId,
        sender_id: prefill?.reportedBy || user.id,
        title: title.trim(),
        location: location.trim(),
        description: description.trim(),
        priority,
        media_ids: [...(prefill?.mediaIds || []), ...mediaItems.map((m) => m.id)],
        conversation_id: conversation?.id || null,
      });
      toast.success(t("issue") + " \u2713");
      setTitle(""); setLocation(""); setDescription(""); setMediaItems([]);
      onCreated && onCreated();
      onClose();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="issue-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" /> {t("report_issue")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Voice-first: big mic button — speak the problem, AI drafts the issue */}
          {voiceState === "recording" ? (
            <button
              type="button"
              data-testid="issue-voice-stop"
              onClick={stopVoice}
              className="w-full h-14 rounded-xl bg-destructive text-white flex items-center justify-center gap-3 font-semibold animate-pulse"
            >
              <SquareIcon className="h-5 w-5" /> {t("listening_tap_stop")}
            </button>
          ) : voiceState === "processing" ? (
            <div
              data-testid="issue-voice-processing"
              className="w-full h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center gap-3 font-semibold"
            >
              <Loader2 className="h-5 w-5 animate-spin" /> {t("creating_title")}
            </div>
          ) : (
            <button
              type="button"
              data-testid="issue-voice-start"
              onClick={startVoice}
              className="w-full h-14 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-3 font-semibold transition-colors"
            >
              <Mic className="h-6 w-6" /> {voiceState === "ready" ? t("speak_again") : t("speak_issue")}
            </button>
          )}

          {/* After the AI draft: quick question — add media or create right away */}
          {voiceState === "ready" && (
            <div data-testid="issue-voice-ready-panel" className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-primary">
                <Sparkles className="h-4 w-4" /> {t("draft_ready")}
              </div>
              <div className="text-sm font-semibold" data-testid="issue-voice-draft-title">{title}</div>
              <div className="text-xs text-muted-foreground">{t("add_media_question")}</div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="issue-voice-add-media"
                  onClick={() => mediaRef.current?.click()}
                  disabled={uploadingMedia}
                >
                  <Camera className="h-4 w-4 mr-1.5 text-primary" /> {uploadingMedia ? `${t("uploading")}...` : t("add_photo_video")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  data-testid="issue-voice-create-now"
                  onClick={submit}
                  disabled={saving || uploadingMedia || !title.trim()}
                  className="bg-destructive hover:bg-destructive/90 text-white"
                >
                  <CheckSquare className="h-4 w-4 mr-1.5" /> {saving ? t("sending") : t("create_now")}
                </Button>
              </div>
            </div>
          )}

          {prefill?.reportedByName && (
            <div className="text-xs text-muted-foreground">{t("reported_by")}: <span className="font-medium text-foreground">{prefill.reportedByName}</span></div>
          )}
          <div>
            <Label className="text-xs">{t("title")}</Label>
            <Input data-testid="issue-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Opening incorrect" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("location")}</Label>
              <Input data-testid="issue-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Front Door" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">{t("priority")}</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="issue-priority-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high" data-testid="issue-priority-high">{t("high")}</SelectItem>
                  <SelectItem value="medium">{t("medium")}</SelectItem>
                  <SelectItem value="low">{t("low")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">{t("description")}</Label>
            <div className="flex items-start gap-2 mt-1">
              <Textarea data-testid="issue-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="flex-1" placeholder="Opening is 1/2 inch smaller than measurement..." />
              <VoiceInputButton onTranscript={(txt) => setDescription((prev) => (prev ? `${prev} ${txt}` : txt))} className="mt-1 shrink-0" />
            </div>
          </div>
          {/* Photo/video attachments */}
          <div>
            <input ref={mediaRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={onPickMedia} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="issue-add-media-button"
              onClick={() => mediaRef.current?.click()}
              disabled={uploadingMedia}
              className="w-full justify-start"
            >
              <Camera className="h-4 w-4 mr-2 text-primary" />
              {uploadingMedia ? `${t("uploading")}...` : t("add_photo_video")}
            </Button>
            {mediaItems.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-2" data-testid="issue-media-previews">
                {mediaItems.map((m) => (
                  <div key={m.id} className="relative" data-testid="issue-media-item">
                    {m.kind === "video" ? (
                      <div className="h-16 w-16 rounded-lg border bg-muted flex flex-col items-center justify-center gap-0.5">
                        <Video className="h-5 w-5 text-primary" />
                        <span className="text-[8px] text-muted-foreground px-1 truncate max-w-[60px]">{m.name}</span>
                      </div>
                    ) : (
                      <img src={m.preview} alt="" className="h-16 w-16 object-cover rounded-lg border" />
                    )}
                    <button
                      data-testid="issue-media-remove"
                      onClick={() => setMediaItems((prev) => prev.filter((x) => x.id !== m.id))}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-white flex items-center justify-center shadow"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button data-testid="issue-submit" onClick={submit} disabled={saving || uploadingMedia || !title.trim()} className="bg-destructive hover:bg-destructive/90 text-white">
            {saving ? t("sending") : t("create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TaskFromMessageDialog({ open, onClose, jobId, conversationId, participants, message, onCreated }) {
  const { user, t } = useApp();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const [jobInstallerId, setJobInstallerId] = useState("");

  useEffect(() => {
    if (open && message) {
      setTitle((message.content || "").slice(0, 100));
    }
    if (open) setVoiceReady(false);
  }, [open, message]);

  // Default "Assign to" = the installer linked to this job (crew leader first)
  useEffect(() => {
    if (!open || !jobId) return;
    let alive = true;
    api.get(`/jobs/${jobId}`).then((res) => {
      if (!alive) return;
      const j = res.data;
      const candidates = [j.crew?.leader_id, ...(j.installer_ids || [])].filter(Boolean);
      const def = candidates.find((id) => (participants || []).some((p) => p.id === id)) || candidates[0] || "";
      setJobInstallerId(def);
      setAssignee((prev) => prev || def);
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  const submit = async () => {
    if (!title.trim() || !assignee || !jobId) return;
    setSaving(true);
    try {
      const photoMedia = (message?.media || []).find((m) => m.kind === "image");
      await api.post("/tasks", {
        job_id: jobId,
        title: title.trim(),
        description: description.trim(),
        assigned_to: assignee,
        created_by: user.id,
        due_label: due.trim(),
        photo_media_id: photoMedia?.id || null,
        conversation_id: conversationId || null,
      });
      toast.success(t("task") + " \u2713");
      setTitle(""); setDescription(""); setDue("");
      onCreated && onCreated();
      onClose();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="task-from-message-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-accent-foreground" /> {t("create_task")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Voice-first: speak the task -> AI fills title/description/assignee/due */}
          <VoiceDraftButton
            kind="task"
            label={voiceReady ? t("speak_again") : t("speak_task")}
            participants={participants}
            onDraft={(d) => {
              if (d.title) setTitle(d.title);
              if (d.description) setDescription(d.description);
              // Spoken name wins; otherwise default to the job's linked installer
              if (d.assignee_id) setAssignee(d.assignee_id);
              else setAssignee((prev) => prev || jobInstallerId);
              if (d.due_label) setDue(d.due_label);
              setVoiceReady(true);
            }}
          />
          {voiceReady && (
            <div data-testid="task-voice-ready-panel" className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-primary">
                <Sparkles className="h-4 w-4" /> {t("draft_ready")}
              </div>
              <div className="text-sm font-semibold">{title}</div>
              <Button
                type="button"
                size="sm"
                data-testid="task-voice-create-now"
                onClick={submit}
                disabled={saving || !title.trim() || !assignee}
                className="w-full"
              >
                <CheckSquare className="h-4 w-4 mr-1.5" /> {saving ? t("sending") : t("task_create_now")}
              </Button>
              {!assignee && <div className="text-[11px] text-muted-foreground">{t("pick_assignee_hint")}</div>}
            </div>
          )}
          <div>
            <Label className="text-xs">{t("title")}</Label>
            <Input data-testid="task-msg-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">{t("description")}</Label>
            <div className="flex items-start gap-2 mt-1">
              <Textarea data-testid="task-msg-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="flex-1" />
              <VoiceInputButton onTranscript={(txt) => setDescription((prev) => (prev ? `${prev} ${txt}` : txt))} className="mt-1 shrink-0" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("assign_to")}</Label>
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger data-testid="task-msg-assignee-select" className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {(participants || []).map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`task-msg-assignee-${p.id}`}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("due")}</Label>
              <Input data-testid="task-msg-due-input" value={due} onChange={(e) => setDue(e.target.value)} placeholder={t("due_placeholder")} className="mt-1" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button data-testid="task-msg-submit" onClick={submit} disabled={saving || !title.trim() || !assignee}>{saving ? t("sending") : t("create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// MeasurementDialog moved to its own file (multi-unit flow + adjustable boxes)
export { MeasurementDialog } from "./MeasurementDialog";

export function MaterialRequestDialog({ open, onClose, conversation, extraMeta }) {
  const { user, t } = useApp();
  const [items, setItems] = useState("");
  const [neededBy, setNeededBy] = useState("");
  const [saving, setSaving] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);

  useEffect(() => {
    if (open) setVoiceReady(false);
  }, [open]);

  const submit = async () => {
    if (!items.trim()) return;
    setSaving(true);
    try {
      await sendMessage({
        conversation_id: conversation.id,
        sender_id: user.id,
        type: "material_request",
        content: items.trim(),
        meta: { items: items.trim(), needed_by: neededBy.trim(), ...(extraMeta || {}) },
      });
      setItems(""); setNeededBy("");
      onClose();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="material-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Package className="h-5 w-5 text-accent-foreground" /> {t("material_request")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Voice-first: speak the material request -> AI formats items + timing */}
          <VoiceDraftButton
            kind="material"
            label={voiceReady ? t("speak_again") : t("speak_material")}
            onDraft={(d) => {
              if (d.items) setItems(d.items);
              if (d.needed_by) setNeededBy(d.needed_by);
              setVoiceReady(true);
            }}
          />
          {voiceReady && (
            <div data-testid="material-voice-ready-panel" className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-primary">
                <Sparkles className="h-4 w-4" /> {t("draft_ready")}
              </div>
              <Button
                type="button"
                size="sm"
                data-testid="material-voice-send-now"
                onClick={submit}
                disabled={saving || !items.trim()}
                className="w-full"
              >
                <Package className="h-4 w-4 mr-1.5" /> {saving ? t("sending") : t("send_now")}
              </Button>
            </div>
          )}
          <div>
            <Label className="text-xs">{t("items")}</Label>
            <div className="flex items-start gap-2 mt-1">
              <Textarea data-testid="material-items-input" value={items} onChange={(e) => setItems(e.target.value)} rows={3} placeholder="2x OSI Quad sealant (white)" className="flex-1" />
              <VoiceInputButton onTranscript={(txt) => setItems((prev) => (prev ? `${prev} ${txt}` : txt))} className="mt-1 shrink-0" />
            </div>
          </div>
          <div>
            <Label className="text-xs">{t("needed_by")}</Label>
            <Input data-testid="material-needed-input" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} placeholder="Tomorrow 8 AM" className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button data-testid="material-submit" onClick={submit} disabled={saving || !items.trim()}>{saving ? t("sending") : t("send")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QuestionDialog({ open, onClose, conversation, extraMeta }) {
  const { user, t } = useApp();
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await sendMessage({
        conversation_id: conversation.id,
        sender_id: user.id,
        type: "question",
        content: text.trim(),
        meta: { ...(extraMeta || {}) },
      });
      setText("");
      onClose();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="question-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" /> {t("question")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-start gap-2">
          <Textarea data-testid="question-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} autoFocus className="flex-1" />
          <VoiceInputButton onTranscript={(txt) => setText((prev) => (prev ? `${prev} ${txt}` : txt))} className="mt-1 shrink-0" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button data-testid="question-submit" onClick={submit} disabled={saving || !text.trim()}>{saving ? t("sending") : t("send")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PhotoSendDialog({ open, onClose, dataUrl, dataUrls, isJobConv, job, onSend, onTakeAnother, appendDataUrl }) {
  const { t } = useApp();
  const [category, setCategory] = useState("during");
  const [markupOpen, setMarkupOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  // One entry per selected photo: { src, edited, overlays, caption }
  const [photos, setPhotos] = useState([]);
  const [idx, setIdx] = useState(0);
  // Proof-of-work stamp (date + time + job name) — default ON, remembered per device
  const [stamp, setStamp] = useState(() => {
    try { return localStorage.getItem("osc_photo_stamp") !== "0"; } catch (e) { return true; }
  });
  const toggleStamp = (v) => {
    setStamp(v);
    try { localStorage.setItem("osc_photo_stamp", v ? "1" : "0"); } catch (e) { /* ignore */ }
  };
  // "Save to gallery" — user's choice, remembered per device (native only)
  const [saveGallery, setSaveGallery] = useState(getSaveToGalleryPref);
  const toggleGallery = (v) => { setSaveGallery(v); setSaveToGalleryPref(v); };

  useEffect(() => {
    if (!open) return;
    const list = (dataUrls && dataUrls.length ? dataUrls : (dataUrl ? [dataUrl] : []));
    setPhotos(list.map((src) => ({ src, edited: null, overlays: null, caption: "" })));
    setIdx(0);
    setCategory("during");
  }, [open, dataUrl, dataUrls]);

  useEffect(() => {
    if (!open || !appendDataUrl) return;
    setPhotos((prev) => {
      if (prev.some((p) => p.src === appendDataUrl)) return prev;
      const next = [...prev, { src: appendDataUrl, edited: null, overlays: null, caption: "" }];
      setIdx(next.length - 1);
      return next;
    });
  }, [appendDataUrl, open]);

  const cur = photos[idx];
  const currentImage = cur ? (cur.edited || cur.src) : null;
  const patchCur = (patch) => setPhotos((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const removeAt = (i) => {
    setPhotos((prev) => prev.filter((_, k) => k !== i));
    setIdx((k) => Math.max(0, Math.min(k > i ? k - 1 : k, photos.length - 2)));
  };

  const handleSend = async () => {
    if (!photos.length) return;
    setSending(true);
    try {
      for (let i = 0; i < photos.length; i++) {
        const p = photos[i];
        setProgress(i + 1);
        await onSend({ originalDataUrl: p.src, editedDataUrl: p.edited, category: isJobConv ? category : null, caption: p.caption.trim(), textOverlays: p.overlays, stamp });
      }
      if (saveGallery && galleryAvailable()) {
        const n = await saveToGallery(photos.map((p) => ({ path: p.edited || p.src, kind: "photo" })), job);
        if (n > 0) toast.success(t("saved_to_gallery_n").replace("{n}", n));
        else toast.error(t("gallery_save_failed"));
      }
      setPhotos([]); setCategory("during");
      onClose();
    } finally {
      setSending(false);
      setProgress(0);
    }
  };

  if (!open || !cur) return null;
  const multi = photos.length > 1;

  return (
    <>
      {/* modal={false} while the markup editor is open: the editor lives in a portal
          OUTSIDE this dialog, and Radix's focus trap would steal focus from its text
          input (keyboard never opens on mobile). */}
      <Dialog open={open} modal={!markupOpen} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          className="max-w-md"
          data-testid="photo-send-dialog"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => { if (markupOpen) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              {multi ? t("send_photos_n").replace("{n}", photos.length) : t("send_photo")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <img src={currentImage} alt="" className="w-full max-h-64 object-contain rounded-lg border bg-muted" data-testid="photo-send-preview" />
              {multi && (
                <span className="absolute top-2 left-2 rounded-full bg-black/60 text-white text-[11px] font-semibold px-2 py-0.5" data-testid="photo-send-counter">
                  {idx + 1}/{photos.length}
                </span>
              )}
              <div className="absolute bottom-2 right-2 flex gap-1.5">
                <Button
                  size="sm"
                  variant="secondary"
                  data-testid="photo-save-device-button"
                  onClick={async () => {
                    const r = await saveImageDataUrl(currentImage, mediaFilename(null, job));
                    if (r === "downloaded") toast.success(t("saved_to_device"));
                    else if (r === false) toast.error("Failed");
                  }}
                >
                  <Download className="h-4 w-4 mr-1.5" /> {t("save_to_device")}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  data-testid="photo-send-markup-button"
                  onClick={() => setMarkupOpen(true)}
                >
                  <PenLine className="h-4 w-4 mr-1.5" /> {t("markup")}
                </Button>
              </div>
            </div>
            {(multi || onTakeAnother) && (
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 min-w-0" data-testid="photo-send-strip">
                {onTakeAnother && photos.length < 20 && (
                  <button
                    type="button"
                    data-testid="photo-take-another"
                    onClick={onTakeAnother}
                    className="h-14 w-14 shrink-0 rounded-lg border-2 border-dashed border-primary/50 text-primary flex flex-col items-center justify-center gap-0.5 hover:bg-primary/5"
                  >
                    <Camera className="h-5 w-5" />
                    <span className="text-[9px] font-semibold leading-none">{t("take_another")}</span>
                  </button>
                )}
                {photos.map((p, i) => (
                  <div key={i} className="relative shrink-0">
                    <button
                      type="button"
                      data-testid={`photo-send-thumb-${i}`}
                      onClick={() => setIdx(i)}
                      className={`h-14 w-14 rounded-lg overflow-hidden border-2 transition-colors ${i === idx ? "border-primary" : "border-transparent opacity-80"}`}
                    >
                      <img src={p.edited || p.src} alt="" className="h-full w-full object-cover" />
                    </button>
                    <button
                      type="button"
                      aria-label="remove"
                      data-testid={`photo-send-remove-${i}`}
                      onClick={() => removeAt(i)}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-foreground text-background flex items-center justify-center shadow"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {isJobConv && (
              <div>
                <Label className="text-xs">{t("category")}</Label>
                <div className="flex gap-1.5 flex-wrap mt-1.5">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      data-testid={`photo-category-${cat}`}
                      onClick={() => setCategory(cat)}
                      className={`rounded-full px-3 py-1.5 text-xs border font-medium transition-colors ${category === cat ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}
                    >
                      {t(cat === "issue" ? "issue_cat" : cat)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">{t("caption")}{multi ? ` (${idx + 1}/${photos.length})` : ""}</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input data-testid="photo-caption-input" value={cur.caption} onChange={(e) => patchCur({ caption: e.target.value })} className="flex-1" />
                <VoiceInputButton onTranscript={(txt) => patchCur({ caption: cur.caption ? `${cur.caption} ${txt}` : txt })} className="shrink-0" />
              </div>
            </div>
            {/* Proof-of-work stamp: date + time + job name burned into the photo */}
            <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <Stamp className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm font-medium truncate">{t("photo_stamp")}</span>
              </div>
              <Switch data-testid="photo-stamp-toggle" checked={stamp} onCheckedChange={toggleStamp} />
            </div>
            {galleryAvailable() && (
              <label className="flex items-center gap-2.5 px-1 cursor-pointer select-none" data-testid="photo-save-gallery-row">
                <Checkbox data-testid="photo-save-gallery-toggle" checked={saveGallery} onCheckedChange={(v) => toggleGallery(!!v)} />
                <span className="text-sm">{t("save_to_gallery")}</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
            <Button data-testid="photo-send-submit" onClick={handleSend} disabled={sending}>
              {sending ? (multi ? `${t("sending")} ${progress}/${photos.length}` : t("sending")) : (multi ? t("send_photos_n").replace("{n}", photos.length) : t("send"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MarkupEditor open={markupOpen} imageSrc={currentImage} onSave={(du, ovs) => { patchCur({ edited: du, overlays: ovs && ovs.length ? ovs : null }); setMarkupOpen(false); }} onClose={() => setMarkupOpen(false)} />
    </>
  );
}
